/**
 * /setup — screen 3v. Username, team, terms, and an optional email.
 *
 * D2 said name and team and nothing else, and the bill came later: a guest with
 * no address on file cannot get back in from a second device, and cannot stop
 * anyone else walking into their account. The field is optional and blocks
 * nothing — the account is created and signed in whether or not it is filled —
 * but it is asked for here, where it costs one line, rather than in Settings,
 * where nobody went.
 *
 * Login is offered first, above the form. Once a group exists, coming back is
 * the more common arrival than signing up, and it used to be buried.
 *
 * Availability is checked live, debounced 400ms, because discovering that
 * "Ryan" is taken after tapping Continue is the single most annoying way to
 * learn it. A collision offers up to three suggestions from the server rather
 * than making the user guess again.
 *
 * D20 — the Terms and Privacy links open in a new tab with
 * `rel="noopener noreferrer"`, the checkbox starts unchecked, and Continue is
 * dead until it is ticked. The server re-checks acceptance regardless: the
 * disabled button is courtesy, not enforcement.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TEAMS, teamLogoUrl } from '@shared/teams'
import { useStore } from '../lib/store'
import { useTheme } from '../theme/ThemeProvider'
import { ApiFailure, GET, POST } from '../lib/api'
import { Button, Screen, Spacer } from '../ui'
import { useNavigate } from 'react-router-dom'

const TERMS_VERSION = 1
const DEBOUNCE_MS = 400

interface NameAvailability {
  available: boolean
  reason?: string
  message?: string
  suggestions?: string[]
}

type NameStatus =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'ok' }
  | { kind: 'taken'; message: string; suggestions: string[] }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function ProfileSetup() {
  const { refresh } = useStore()
  const { previewTeam } = useTheme()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [status, setStatus] = useState<NameStatus>({ kind: 'idle' })
  const [teamId, setTeamId] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [email, setEmail] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [needsRestart, setNeedsRestart] = useState(false)

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inflight = useRef<AbortController | null>(null)

  // ── live availability, debounced 400ms ────────────────────────────────────

  const check = useCallback(async (candidate: string) => {
    inflight.current?.abort()
    const ac = new AbortController()
    inflight.current = ac
    setStatus({ kind: 'checking' })
    try {
      const res = await GET<NameAvailability>(
        `/api/auth/name-available?name=${encodeURIComponent(candidate)}`, ac.signal,
      )
      if (ac.signal.aborted) return
      if (res.available) setStatus({ kind: 'ok' })
      else {
        setStatus({
          kind: 'taken',
          message: res.message ?? 'That name is taken.',
          suggestions: (res.suggestions ?? []).slice(0, 3),
        })
      }
    } catch (e) {
      if (ac.signal.aborted) return
      // A failed check is not a failed name. Stay quiet and let the server rule
      // on it at submit time.
      setStatus({ kind: 'idle' })
      if (e instanceof ApiFailure && e.code === 'rate_limited') {
        setStatus({ kind: 'idle' })
      }
    }
  }, [])

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      inflight.current?.abort()
      setStatus({ kind: 'idle' })
      return
    }
    timer.current = setTimeout(() => { void check(trimmed) }, DEBOUNCE_MS)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [name, check])

  useEffect(() => () => { inflight.current?.abort() }, [])

  // ── team grid ─────────────────────────────────────────────────────────────

  // Repaint the whole app the moment a logo is tapped, so the choice is felt
  // rather than described. The preview is dropped on unmount: either Continue
  // succeeded and the real team took over, or they left and it was never theirs.
  useEffect(() => {
    previewTeam(teamId)
  }, [teamId, previewTeam])

  useEffect(() => () => { previewTeam(null) }, [previewTeam])

  const teams = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return TEAMS
    return TEAMS.filter((t) =>
      t.location.toLowerCase().includes(needle)
      || t.nickname.toLowerCase().includes(needle)
      || t.abbr.toLowerCase().includes(needle))
  }, [q])

  // ── submit ────────────────────────────────────────────────────────────────

  // An empty box is fine; a half-typed address is not. Validating only what
  // was actually entered keeps the field optional without letting a typo
  // silently cost someone their only way back in.
  const emailOk = email.trim() === '' || EMAIL_RE.test(email.trim())
  const canContinue = agreed && !!teamId && name.trim().length >= 2
    && status.kind !== 'taken' && emailOk && !busy

  const submit = useCallback(async () => {
    if (!teamId) return
    setBusy(true)
    setSubmitError(null)
    try {
      await POST('/api/auth/profile', {
        displayName: name.trim(),
        teamId,
        termsAccepted: true,
        termsVersion: TERMS_VERSION,
        email: email.trim() ? email.trim() : null,
      })
      await refresh()
    } catch (e) {
      if (e instanceof ApiFailure && (e.code === 'name_taken' || e.code === 'name_held')) {
        setStatus({ kind: 'taken', message: e.message, suggestions: [] })
        setSubmitError(null)
      } else if (e instanceof ApiFailure && e.code === 'unauthenticated') {
        // The passcode step handed us a session and it didn't survive to here.
        // The usual cause is a browser that refused the cookie — which happens
        // whenever the page isn't in a secure context, because the session
        // cookie is `Secure`. Say which it is instead of stranding them on a
        // screen whose only button no longer does anything.
        setSubmitError(
          typeof window !== 'undefined' && !window.isSecureContext
            ? 'This page is loaded over an insecure connection, so your browser is refusing to keep you signed in. Open the site at https:// and start again.'
            : 'Your sign-in didn’t stick. Your browser may be blocking cookies for this site. Check that cookies are allowed, then start again.',
        )
        setNeedsRestart(true)
      } else {
        setSubmitError(e instanceof ApiFailure ? e.message : 'Couldn’t set that up. Try again.')
      }
      setBusy(false)
    }
  }, [email, name, refresh, teamId])

  return (
    <Screen>
      {/* `overflow-x: hidden` because a scroll container with `overflow-y: auto`
          computes its other axis to `auto` too, so any one-pixel overhang turns
          into a sideways swipe on a phone. Nothing here is meant to scroll
          horizontally. */}
      <div className="scr" style={{ flex: '1 1 auto', padding: '26px 20px 20px', overflowX: 'hidden' }}>
        {/* Returning is the more common arrival than signing up, once a group
            exists, and it used to be buried under the form. */}
        {/* `?signin=1`, not bare `/join`: the bare route opens on the promo
            code, which is the one thing somebody who already has an account
            does not have and does not need. */}
        <Button onClick={() => navigate('/join?signin=1')}>Login</Button>

        <div
          style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '22px 0 24px' }}
          aria-hidden
        >
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          <span className="mono" style={{ fontSize: 11, color: 'var(--dim)', letterSpacing: '.14em' }}>
            OR
          </span>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
          <label htmlFor="setup-name">Username</label>
        </h1>
        <input
          id="setup-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={24}
          autoComplete="nickname"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          aria-describedby="setup-name-status"
          placeholder="Make it unique"
          style={{
            width: '100%', marginTop: 12, padding: '15px 16px', borderRadius: 14, minHeight: 48,
            border: `1px solid ${status.kind === 'taken' ? 'var(--alert-line)' : 'var(--line)'}`,
            background: 'var(--surf)', fontSize: 15, color: 'var(--fg)',
          }}
        />

        <div id="setup-name-status" aria-live="polite" style={{ minHeight: 20, marginTop: 8 }}>
          {status.kind === 'ok' ? (
            <span style={{ fontSize: 12, color: 'var(--bright)' }}>✓ Available</span>
          ) : status.kind === 'checking' ? (
            <span style={{ fontSize: 12, color: 'var(--dim)' }}>Checking…</span>
          ) : status.kind === 'taken' ? (
            <span style={{ fontSize: 12, color: 'var(--alert-fg)' }}>{status.message}</span>
          ) : null}
        </div>

        {status.kind === 'taken' && status.suggestions.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {status.suggestions.map((s) => (
              <button
                key={s}
                onClick={() => setName(s)}
                className="mono"
                style={{
                  fontSize: 11, fontWeight: 600, padding: '8px 14px', borderRadius: 999,
                  minHeight: 44, border: '1px solid var(--line)', color: 'var(--bright)',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}

        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '28px 0 0', letterSpacing: '-0.02em' }}>
          Who do you root for?
        </h2>
        <label htmlFor="setup-team-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Search teams
        </label>
        <input
          id="setup-team-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search city, nickname, or abbreviation"
          autoCorrect="off"
          spellCheck={false}
          style={{
            width: '100%', marginTop: 12, padding: '12px 14px', borderRadius: 12, minHeight: 44,
            border: '1px solid var(--line)', background: 'transparent', fontSize: 13, color: 'var(--fg)',
          }}
        />

        <div
          role="radiogroup"
          aria-label="Your team"
          // `minmax(0, 1fr)`, not `1fr`: a grid track's automatic minimum is the
          // item's min-content size, and a 500px ESPN logo reports exactly that,
          // so plain `1fr` blows the grid past the viewport (this is what made
          // the page scroll sideways).
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 12 }}
        >
          {teams.map((t) => (
            <TeamTile
              key={t.id}
              teamId={t.id}
              abbr={t.abbr}
              name={`${t.location} ${t.nickname}`}
              selected={teamId === t.id}
              onSelect={() => setTeamId(t.id)}
            />
          ))}
        </div>
        {teams.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 14 }}>No team matches “{q}”.</div>
        ) : null}

        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '28px 0 0', letterSpacing: '-0.02em' }}>
          <label htmlFor="setup-email">Email (Optional)</label>
        </h2>
        <input
          id="setup-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          aria-describedby="setup-email-help"
          placeholder="you@example.com"
          style={{
            width: '100%', marginTop: 12, padding: '15px 16px', borderRadius: 14, minHeight: 48,
            border: `1px solid ${emailOk ? 'var(--line)' : 'var(--alert-line)'}`,
            background: 'var(--surf)', fontSize: 15, color: 'var(--fg)',
          }}
        />
        <div
          id="setup-email-help"
          style={{ fontSize: 13, color: emailOk ? 'var(--dim)' : 'var(--alert-fg)', marginTop: 8, lineHeight: 1.5 }}
        >
          {emailOk ? 'Email is used to log back in again.' : 'That email doesn’t look right.'}
        </div>

        {/* A padded row rather than a bare checkbox floating in the gutter: the
            whole 60px-tall band is the tap target (D112 asks for 44), which is
            also why the box itself no longer needs the negative-margin trick
            that used to eat the space around it. Links inside the label are
            excluded from the toggle so they still open. */}
        <div
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) return
            setAgreed((v) => !v)
          }}
          style={{
            marginTop: 30, display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer',
            minHeight: 60, padding: '16px 16px', borderRadius: 14,
            border: `1px solid ${agreed ? 'var(--fill)' : 'var(--line)'}`,
            background: agreed ? 'var(--surf)' : 'transparent',
          }}
        >
          <button
            role="checkbox"
            aria-checked={agreed}
            aria-labelledby="setup-terms-label"
            onClick={(e) => { e.stopPropagation(); setAgreed((v) => !v) }}
            style={{
              width: 24, height: 24, borderRadius: 7, flex: '0 0 auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: agreed ? '1px solid var(--fill)' : '1px solid var(--line)',
              background: agreed ? 'var(--fill)' : 'transparent',
              color: 'var(--ink)', fontSize: 14, fontWeight: 800, lineHeight: 1,
            }}
          >
            {agreed ? '✓' : ''}
          </button>
          <div id="setup-terms-label" style={{ fontSize: 14, lineHeight: 1.5, minWidth: 0 }}>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bright)' }}>
              Terms of Use
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bright)' }}>
              Privacy Policy
            </a>
          </div>
        </div>

        {submitError ? (
          <div style={{ marginTop: 14 }} role="alert">
            <div style={{ fontSize: 13, color: 'var(--alert-fg)', lineHeight: 1.5 }}>{submitError}</div>
            {needsRestart ? (
              <button
                onClick={() => {
                  const here = new URL(window.location.href)
                  here.protocol = 'https:'
                  here.pathname = '/join'
                  window.location.replace(here.toString())
                }}
                style={{
                  marginTop: 12, width: '100%', padding: 14, borderRadius: 'var(--radius-btn)',
                  background: 'var(--fill)', color: 'var(--ink)', fontWeight: 700, fontSize: 15, minHeight: 48,
                }}
              >
                Start again over https
              </button>
            ) : null}
          </div>
        ) : null}

        <Spacer h={24} />
      </div>

      {/* Sticky footer: `line` ground and `dim` text until it is actually usable. */}
      <div style={{
        flex: '0 0 auto', padding: '12px 20px calc(24px + env(safe-area-inset-bottom, 0px))',
        borderTop: '1px solid var(--line)', background: 'var(--bg)',
      }}>
        <button
          onClick={() => { void submit() }}
          disabled={!canContinue}
          style={{
            display: 'block', width: '100%', textAlign: 'center', padding: 15, minHeight: 48,
            borderRadius: 14, fontSize: 15, fontWeight: 700,
            background: canContinue ? 'var(--fill)' : 'var(--line)',
            color: canContinue ? 'var(--ink)' : 'var(--dim)',
          }}
        >
          {busy ? 'Setting up…' : 'Continue'}
        </button>
      </div>
    </Screen>
  )
}

/** A square tile with the ESPN logo; the abbreviation is the alt and fallback. */
function TeamTile({
  teamId, abbr, name, selected, onSelect,
}: {
  teamId: string; abbr: string; name: string; selected: boolean; onSelect: () => void
}) {
  const [broken, setBroken] = useState(false)
  const src = teamLogoUrl(teamId)
  return (
    <button
      role="radio"
      aria-checked={selected}
      aria-label={name}
      onClick={onSelect}
      style={{
        aspectRatio: '1', width: '100%', minWidth: 0, borderRadius: 12, padding: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: selected ? 'var(--surf)' : 'transparent',
        border: selected ? '2px solid var(--fill)' : '1px solid var(--line)',
      }}
    >
      {broken || !src ? (
        <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: selected ? 'var(--fg)' : 'var(--dim)' }}>
          {abbr}
        </span>
      ) : (
        <img
          src={src}
          alt={abbr}
          loading="lazy"
          onError={() => setBroken(true)}
          style={{ width: '78%', height: '78%', objectFit: 'contain' }}
        />
      )}
    </button>
  )
}
